import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SurveyInviteClient, SurveyInviteDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new SurveyInviteClient(API_BASE_URL, axiosInstance);

// Query hook - Get all surveyinvites for dropdowns (cached 4 hours)
export const useSurveyInvites = () => {
  return useQuery({
    queryKey: ['surveyinvites'],
    queryFn: async () => {
      const response = await apiClient.searchSurveyInvites({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'Title',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Direct API search (no caching) - for admin tables with server-side paging
export const useSurveyInviteSearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchSurveyInvites({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single surveyInvite by ID
export const useSurveyInviteById = (id: string) => {
  return useQuery({
    queryKey: ['surveyinvites', id],
    queryFn: async () => {
      const response = await apiClient.getSurveyInviteById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
};

// Mutation hook - Create surveyInvite
export const useCreateSurveyInvite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SurveyInviteDTO) => {
      await apiClient.createSurveyInvite(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surveyinvites'] });
    },
  });
};

// Mutation hook - Update surveyInvite
export const useUpdateSurveyInvite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SurveyInviteDTO }) => {
      await apiClient.updateSurveyInvite(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surveyinvites'] });
    },
  });
};

// Mutation hook - Delete surveyInvite
export const useDeleteSurveyInvite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteSurveyInvite(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surveyinvites'] });
    },
  });
};
